import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ApiUrl from "../Environment/ApiUrl";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useToast } from "../Toast/ToastContext";

function Sales() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [selectedSalesId, setSelectedSalesId] = useState(null);

  // ── Daily rates cache (fetched once, keyed by date) ──────────
  // We never expose these raw values in the UI — they are only
  // used internally to compute the purity-adjusted 1g rate.
  const [dailyRatesByDate, setDailyRatesByDate] = useState({});

  const fetchDailyRates = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/daily_rates.php`);
      const rows = response.data?.data || [];
      const map = {};
      rows.forEach((r) => {
        map[r.date] = {
          gold: parseFloat(r.gold_1g_rate) || 0,       // already per 1g
          silver: (parseFloat(r.silver_10g_rate) || 0), // convert to per 1g
        };
      });
      setDailyRatesByDate(map);
    } catch (error) {
      console.error("Error fetching daily rates:", error.message);
    }
  };

  // ── Helper: returns the "pure rate per 1g" for an item's category/date ──
  // This is the backend-facing value (gold_1g_rate or silver per-gram rate).
  // It is NEVER rendered directly — only fed into calculateRate1g below.
  const getPureRatePerGram = (itemObj, salesDate) => {
    if (!itemObj) return 0;

    // 1. Prefer a pure_rate already attached to the item by the backend
    if (itemObj.pure_rate !== undefined && itemObj.pure_rate !== null && itemObj.pure_rate !== "") {
      return parseFloat(itemObj.pure_rate) || 0;
    }

    // 2. Otherwise fall back to the daily rate table by sales date + category
    const dayRates = dailyRatesByDate[salesDate];
    if (!dayRates) return 0;

    const category = (itemObj.category || "").toLowerCase();
    if (category.includes("silver")) return dayRates.silver;
    return dayRates.gold; // default to gold
  };

  // ── Core formula ──────────────────────────────────────────────
  // rate_1g   = (purity / 100) * pure_rate
  // amount    = wt * rate_1g * qty
  // purity & pure_rate are the "hidden" backend inputs.
  // rate_1g (and amount) are the only derived values shown to the user.
  const calculateRate1g = (itemObj, salesDate) => {
    if (!itemObj) return 0;
    const purity = parseFloat(itemObj.purity) || 0;
    const pureRate = getPureRatePerGram(itemObj, salesDate);
    return (purity / 100) * pureRate;
  };

  const calculateAmount = (itemObj, salesDate) => {
    if (!itemObj) return 0;
    const wt = parseFloat(itemObj.wt) || 0;
    const qty = parseFloat(itemObj.qty) || 1;
    return wt * calculateRate1g(itemObj, salesDate) * qty;
  };

  const formatRate1g = (itemObj) => {
    if (!itemObj) return "0.00";
    return (parseFloat(itemObj.rate) || 0).toFixed(2);
  };

  // Helper: returns "English / Tamil" or just "English"
  const itemLabel = (itemObj) => {
    if (!itemObj) return "-";
    const eng = itemObj.item_name_english || itemObj.item || "";
    const tam = itemObj.item_name_tamil || "";
    return tam ? `${eng} / ${tam}` : eng;
  };

  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // ── Manager filter state ──────────────────────────────
  const [managerFilter, setManagerFilter] = useState("");

  const openModal = (id, action) => {
    setSelectedSalesId(id);
    setActionType(action);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSalesId(null);
    setActionType("");
    setIsSubmitting(false);
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/notification.php`);
      setNotifications(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
    }
  };

  const checkNotificationStatus = (salesId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === salesId &&
        notif.button_value === buttonValue &&
        notif.status === "accepted" &&
        notif.viewed === "false" &&
        notif.employe_id === userId
    );
  };

  const hasPendingNotification = (salesId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === salesId &&
        notif.button_value === buttonValue &&
        notif.status === "requested" &&
        notif.employe_id === userId &&
        notif.viewed === "false"
    );
  };

  const handlesubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (hasPendingNotification(selectedSalesId, actionType)) {
      closeModal();
      return;
    }
    setLoading(true);
    try {
      const userId = localStorage.getItem("username");
      const currentTime = new Date().toISOString().slice(0, 19).replace("T", " ");
      const formData = {
        table_name: "sales",
        table_id: selectedSalesId,
        employe_id: userId || "",
        status: "requested",
        button_value: actionType,
        time: currentTime,
        viewed: "",
        created_date: currentTime,
        updated_by: userId || "",
        updated_date: currentTime,
      };
      const response = await axios.post(`${ApiUrl}/Api/notification.php`, formData, {
        headers: { "Content-Type": "application/json" },
      });
      setTimeout(() => setLoading(false), 1000);
      closeModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error submitting notification:", error.message);
      toast.error("Error submitting notification. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSales = (id) => {
    const userRole = localStorage.getItem("role");
    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Edit")) {
        navigate(`/update-sales/${id}`);
      } else {
        openModal(id, "Edit");
      }
    } else {
      navigate(`/update-sales/${id}`);
    }
  };

  const [responseData, setResponseData] = useState([]);

  useEffect(() => {
    fetchData();
    fetchNotifications();
    fetchDailyRates();
    const interval = setInterval(() => {
      fetchData();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const localRole = localStorage.getItem("role");
  const localUsername = localStorage.getItem("username");

  const fetchData = async () => {
    try {
      const response = await axios.get(
        localRole === "Employee"
          ? `${ApiUrl}/Api/sales.php?employee_id=${localUsername}`
          : `${ApiUrl}/Api/sales.php`
      );
      setResponseData(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      toast.error("Failed to fetch data", 3000, "topRight");
    }
  };

  const [deleteShowModals, setDeleteShowModals] = useState(false);
  const [getdeleteid, setdeleteid] = useState("");
  const [getdeletename, setdeletename] = useState("");

  const handleprint = (id, sales_no) => navigate(`/bill/${sales_no}`);

  const handleOpenModal = (id, sales_no) => {
    const userRole = localStorage.getItem("role");
    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Delete")) {
        setdeleteid(id);
        setdeletename(sales_no);
        setDeleteShowModals(true);
      } else {
        openModal(id, "Delete");
      }
    } else {
      setdeleteid(id);
      setdeletename(sales_no);
      setDeleteShowModals(true);
    }
  };

  const handleCloseModal = () => {
    setDeleteShowModals(false);
    setdeleteid("");
    setdeletename("");
  };

  const userId = localStorage.getItem("username");

  const handleDelete = async () => {
    try {
      const notificationResponse = await axios.get(
        `${ApiUrl}/Api/notification.php?table_id=${getdeleteid}`
      );
      let notificationData = null;
      if (notificationResponse.data?.status && notificationResponse.data.data?.length > 0) {
        notificationData = notificationResponse.data.data.find(
          (notif) =>
            notif.table_id === getdeleteid.toString() &&
            notif.button_value === "Delete" &&
            notif.viewed === "false"
        );
      }
      const response = await axios.delete(`${ApiUrl}/Api/sales.php/${getdeleteid}`);
      if (response.data?.status) {
        if (notificationData) {
          try {
            const currentTime = new Date().toISOString().slice(0, 19).replace("T", " ");
            await axios.put(
              `${ApiUrl}/Api/notification.php/${notificationData.id}`,
              {
                id: notificationData.id,
                viewed: "true",
                updated_by: userId,
                updated_date: currentTime,
                table_name: notificationData.table_name,
                table_id: notificationData.table_id,
                employe_id: notificationData.employe_id,
                status: notificationData.status,
                button_value: notificationData.button_value,
                time: notificationData.time,
                created_date: notificationData.created_date,
              },
              { headers: { "Content-Type": "application/json" } }
            );
          } catch (notificationError) {
            console.error("Error updating notification:", notificationError);
          }
        }
        handleCloseModal();
        fetchData();
        fetchNotifications();
      } else {
        alert(response.data.message || "Failed to delete sales data");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ── Build unique manager list for dropdown ─────────────────
  const managerList = [...new Set(
    responseData
      .map((item) => item.manager || "")
      .filter(Boolean)
  )].sort();

  // ── Filter data: search + manager ─────────────────────────
  const filteredData = responseData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (item.sales_no && item.sales_no.toLowerCase().includes(searchLower)) ||
      (item.item_list[0]?.category &&
        item.item_list[0]?.category.toLowerCase().includes(searchLower)) ||
      (item.item_list[0]?.item &&
        item.item_list[0]?.item.toLowerCase().includes(searchLower)) ||
      (item.customer_id && item.customer_id.toLowerCase().includes(searchLower)) ||
      (item.status && item.status.toLowerCase().includes(searchLower)) ||
      (item.total && item.total.toLowerCase().includes(searchLower)) ||
      (item.net_total && item.net_total.toLowerCase().includes(searchLower)) ||
      (item.balance && item.balance.toLowerCase().includes(searchLower)) ||
      (item.manager && item.manager.toLowerCase().includes(searchLower));

    const matchesManager = !managerFilter || item.manager === managerFilter;

    return matchesSearch && matchesManager;
  });

  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);

  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 2;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      pages.push(
        <li key={1} className={`page-item ${currentPage === 1 ? "active" : ""}`}>
          <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="start-ellipsis" className="page-item disabled"><span className="page-link">...</span></li>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
        </li>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="end-ellipsis" className="page-item disabled"><span className="page-link">...</span></li>);
      }
      pages.push(
        <li key={totalPages} className={`page-item ${currentPage === totalPages ? "active" : ""}`}>
          <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
        </li>
      );
    }
    return pages;
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Sales Report", 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
      const tableData = filteredData.map((item, index) => [
        index + 1,
        item.sales_no || "",
        item.sales_date || "",
        item.item_list[0]?.category || "",
        itemLabel(item.item_list[0]),
        item.customer_id || "",
        item.manager || "-",
        formatRate1g(item.item_list[0], item.sales_date), // NEW — 1g rate only
        item.total || "",
        item.net_total || "",
      ]);
      autoTable(doc, {
        head: [["S.No", "Sales No", "Sales Date", "Category", "Type", "Customer ID", "Manager", "Rate (1g)","Net Total"]],
        body: tableData,
        startY: 40,
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185] },
        styles: { fontSize: 7, cellPadding: 2 },
      });
      doc.save("sales_report.pdf");
      toast.success("PDF exported successfully!", 2000, "topRight");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Error exporting PDF. Please try again.");
    }
  };

  const exportExcel = () => {
    try {
      const excelData = filteredData.map((item, index) => ({
        "S.No": index + 1,
        "Sales No": item.sales_no || "",
        "Sales Date": item.sales_date || "",
        Category: item.item_list[0]?.category || "",
        Type: itemLabel(item.item_list[0]),
        "Customer ID": item.customer_id || "",
        Manager: item.manager || "-",
        "Rate (1g)": formatRate1g(item.item_list[0], item.sales_date), // NEW
        "Net Total": item.net_total || "",
      }));
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sales");
      XLSX.writeFile(wb, "sales_report.xlsx");
      toast.success("Excel file exported successfully!", 2000, "topRight");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert("Error exporting Excel. Please try again.");
    }
  };

  const exportCSV = () => {
    try {
      const csvData = [
        ["S.No", "Sales No", "Sales Date", "Category", "Type", "Customer ID", "Manager", "Rate (1g)", "Net Total"],
      ];
      filteredData.forEach((item, index) => {
        csvData.push([
          index + 1,
          item.sales_no || "",
          item.sales_date || "",
          item.item_list[0]?.category || "",
          itemLabel(item.item_list[0]),
          item.customer_id || "",
          item.manager || "-",
          formatRate1g(item.item_list[0], item.sales_date), // NEW
          item.net_total || "",
        ]);
      });
      const csvContent = csvData.map((row) => row.map((field) => `"${field}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", "sales_report.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV file exported successfully!", 2000, "topRight");
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const copyToClipboard = () => {
    try {
      const headers = ["S.No", "Sales No", "Sales Date", "Category", "Type", "Customer ID", "Manager", "Rate (1g)", "Total", "Net Total"];
      const rows = filteredData.map((item, index) => [
        index + 1,
        item.sales_no || "",
        item.sales_date || "",
        item.item_list?.[0]?.category || "",
        itemLabel(item.item_list?.[0]),
        item.customer_id || "",
        item.manager || "-",
        formatRate1g(item.item_list?.[0], item.sales_date), // NEW
        item.total || "",
        item.net_total || "",
      ]);
      const formattedText = [headers, ...rows].map((row) => row.map((field) => `"${field}"`).join("\t")).join("\n");
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(formattedText).then(() => {
          toast.success("Data copied to clipboard!", 2000, "topRight");
        });
      }
    } catch (error) {
      console.error("Unexpected error copying to clipboard:", error);
    }
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open("", "_blank");
      const printContent = `
        <!DOCTYPE html><html><head><title>Sales Report</title>
        <style>body{font-family:Arial,sans-serif;margin:20px}table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ddd;padding:6px;text-align:left;font-size:12px}
        th{background:#f2f2f2}</style></head><body>
        <h2>Sales Report</h2><p>Generated: ${new Date().toLocaleDateString()}</p>
        <table><thead><tr>
          <th>S.No</th><th>Sales No</th><th>Sales Date</th><th>Category</th>
          <th>Item</th><th>Customer ID</th><th>Manager</th><th>Rate (1g)</th><th>Net Total</th>
        </tr></thead><tbody>
        ${filteredData.map((item, i) => `<tr>
          <td>${i + 1}</td><td>${item.sales_no || ""}</td><td>${item.sales_date || ""}</td>
          <td>${item.item_list[0]?.category || ""}</td><td>${itemLabel(item.item_list[0])}</td>
          <td>${item.customer_id || ""}</td><td>${item.manager || "-"}</td>
          <td>${formatRate1g(item.item_list[0], item.sales_date)}</td>
        <td>${item.net_total || ""}</td>
        </tr>`).join("")}
        </tbody></table></body></html>`;
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); printWindow.close(); };
      toast.success("Print dialog opened!", 2000, "topRight");
    } catch (error) {
      console.error("Error printing:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  };

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message"><div className="loadinggif"></div></div>
        </div>
      )}
      <div className="container-fluid">
        <div className="d-flex mb-2">
          <h6 className="card-title m-2">Sales</h6>
          <button className="btn btn-primary btn-sm rounded-3" onClick={() => navigate("/add-sales")}>
            Add <span className="ms-2">+</span>
          </button>
        </div>

        <div className="card shadow-sm mb-5">
          <div className="card-body">
            {/* ── Top Controls ── */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mb-2">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label text-dark ">Show:</label>
                <select className="form-control mt-2" style={{ width: "auto" }} onChange={handleItemsPerPageChange} value={showAllItems ? "All" : itemsPerPage}>
                  <option value="All">All</option>
                  {[5, 10, 20, 100, 200, 500, 1000].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* ── Manager Filter Dropdown ── */}
              <div className="d-flex align-items-center gap-2">
                <label className="form-label text-dark">Manager:</label>
                <select
                  className="form-control mt-2"
                  style={{ width: "auto", minWidth: "150px" }}
                  value={managerFilter}
                  onChange={(e) => { setManagerFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Managers</option>
                  {managerList.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex justify-content-center flex-wrap gap-1">
                <button className="btn btn-sm btn-info text-white" onClick={handlePrint}>Print</button>
                <button className="btn btn-sm btn-info text-white" onClick={exportPDF}>PDF</button>
                <button className="btn btn-sm btn-info text-white" onClick={copyToClipboard}>Copy</button>
                <button className="btn btn-sm btn-info text-white" onClick={exportCSV}>CSV</button>
                <button className="btn btn-sm btn-info text-white" onClick={exportExcel}>Excel</button>
              </div>

              <div className="search-container">
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
            </div>

            <div className="card tableCard p-2">
              <div className="table-responsive">
                <table className="table">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No</th>
                      <th>Sales ID</th>
                      <th>Sales Date</th>
                      <th>Category</th>
                      <th>Item Name</th>
                      <th>Customer ID</th>
                      <th>Manager</th>
                      {/* ── NEW Rate (1g) column — purity/pure_rate stay hidden ── */}
                      <th>Rate (1g)</th>
                      <th>Net Total</th>
                      <th>Balance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, index) => {
                      const status = item.notifications?.[0]?.status;
                      let rowClass = "";
                      if (status) {
                        switch (status) {
                          case "accepted": rowClass = "table-secondary"; break;
                          case "rejected": rowClass = "table-danger"; break;
                          case "requested": rowClass = "table-info"; break;
                        }
                      }
                      return (
                        <tr key={item.id} className={rowClass}>
                          <td>{index + 1}</td>
                          <td>{item.sales_no}</td>
                          <td>{formatDate(item.sales_date)}</td>
                          <td>{item.item_list[0]?.category}</td>
                          <td>
                            {(() => {
                              const eng = item.item_list?.[0]?.item_name_english || item.item_list?.[0]?.item || "-";
                              const tam = item.item_list?.[0]?.item_name_tamil || "";
                              return tam ? `${eng} / ${tam}` : eng;
                            })()}
                          </td>
                          <td>{item.customer_id}</td>
                          <td>{item.manager || "-"}</td>
                          {/* ── Derived rate only — never show item.purity / item.pure_rate ── */}
                          <td>₹{formatRate1g(item.item_list[0], item.sales_date)}</td>


                          
                          <td>{item.net_total}</td>
                          <td>{item.balance}</td>
                          <td>
                            <button className="btn btn-sm btn-light me-1" onClick={() => handleUpdateSales(item.id)}>
                              <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                            </button>
                            <button className="btn btn-sm btn-light" onClick={() => handleOpenModal(item.id, item.sales_no)}>
                              <i className="fa-solid fa-trash text-danger fs-6"></i>
                            </button>
                            <button className="btn btn-sm btn-light" onClick={() => handleprint(item.id, item.sales_no)}>
                              <i className="fa-solid fa-print text-secondary fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <p className="text-muted">
                    Showing {showAllItems ? 1 : (currentPage - 1) * itemsPerPage + 1} to{" "}
                    {showAllItems ? totalItems : Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                  </p>
                </div>
                <div className="col-lg-6">
                  <nav aria-label="Page navigation">
                    <ul className="pagination justify-content-end">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(1)}>&laquo;&laquo;</button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>&laquo;</button>
                      </li>
                      {renderPageNumbers()}
                      <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>&raquo;</button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(totalPages)}>&raquo;&raquo;</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            {/* Delete Modal */}
            <div
              className={`modal fade ${deleteShowModals ? "show" : ""}`}
              style={{ display: deleteShowModals ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered modal-confirm">
                <div className="modal-content">
                  <div className="modal-body text-center">
                    <div className="d-flex justify-content-center mb-3">
                      <div className="icon-box d-flex justify-content-center align-items-center bg-light rounded-circle" style={{ width: "80px", height: "80px", margin: "0 auto" }}>
                        <i className="bi bi-exclamation-triangle text-danger fs-2"></i>
                      </div>
                    </div>
                    <h4 className="modal-title mb-3">Are you sure?</h4>
                    <h6 className="mb-4">
                      Do you really want to delete <span className="text-primary">({getdeletename})</span>?
                    </h6>
                    <div className="d-flex btn-group">
                      <button type="button" className="btn btn-secondary btn-sm flex-grow-1" onClick={handleCloseModal}>Cancel</button>
                      <button type="button" className="btn btn-danger btn-sm flex-grow-1 mx-2" onClick={handleDelete} disabled={isSubmitting}>Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Modal */}
            <div
              className={`modal fade ${showModal ? "show" : ""}`}
              style={{ display: showModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h6 className="modal-title text-primary">Send {actionType} Request to Admin</h6>
                    <button type="button" className="btn-close" style={{ fontSize: "10px" }} onClick={closeModal}></button>
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handlesubmit}>
                      <p className="text-center">Do you want to request to {actionType.toLowerCase()} this sale?</p>
                      <div className="text-center mt-4">
                        <button type="button" className="btn btn-secondary btn-sm px-4 me-2" onClick={closeModal}>No</button>
                        <button type="submit" className="btn btn-primary btn-sm px-4" disabled={isSubmitting}>Yes</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {(deleteShowModals || showModal) && <div className="modal-backdrop fade show"></div>}
          </div>
        </div>
      </div>
    </>
  );
}

export default Sales;